import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HiChevronDown as ChevronDownIcon } from 'react-icons/hi2';
import { Popover, RichTextEditor } from '@knack/asterisk-react';
import { type Editor } from '@tiptap/core';
import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';

import { type KnackField } from '@/types/schema/KnackField';
import { useApplicationQuery } from '@/hooks/api/queries/useApplicationQuery';
import { cn } from '@/utils/tailwind';
import { useDataTableStore } from '@/components/data-table/useDataTableStore';
import { replaceTemplateVariables } from '@/components/field-settings/utils/replaceTemplateVariables';
import { FieldSelector } from '@/components/field-settings/utils/rich-text/FieldSelector';
import { FormulaSelector } from '@/components/field-settings/utils/rich-text/FormulaSelector';

type RichTextCodeEditorProps = {
  functionTypes: 'numeric' | 'date' | 'strings';
  content: string;
  onChange: (value: string) => void;
  field: KnackField;
};

function ToolbarDropdownButton({ children, ...props }: { children: React.ReactNode }) {
  return (
    <div
      className="h-6 whitespace-nowrap rounded border border-subtle bg-base p-0.5 px-1 text-sm font-normal"
      {...props}
    >
      <p className="m-0 mt-[-1px] flex items-center gap-0.5 p-0">
        <span>{children}</span>
        <ChevronDownIcon size={18} />
      </p>
    </div>
  );
}

export function RichTextCodeEditor({
  functionTypes,
  content,
  onChange,
  field
}: RichTextCodeEditorProps) {
  const [t] = useTranslation();
  const editorRef = useRef<Editor>();

  const { data: app } = useApplicationQuery();
  const objectKey = useDataTableStore().use.objectKey();

  // Merge the fields of this table with the fields of the connected tables
  // We have two variables, one with the field key and one with the field name that we can later replace with each other
  // On the server we use the keys, but in the editor we want to show the names for better UX
  const { templateKeyVariables, templateNameVariables } = useMemo(() => {
    const table = app?.objects?.find((obj) => obj?.key === objectKey);

    const templateKeyVariablesTemp: string[] = [
      ...(table?.fields.map((currentField) => currentField.key) || [])
    ];
    const templateNameVariablesTemp: string[] = [
      ...(table?.fields.map((currentField) => currentField.name) || [])
    ];

    const connections = [
      ...(table?.connections.outbound.filter((connection) => connection.has !== 'many') || []),
      ...(table?.connections.inbound || [])
    ];

    // eslint-disable-next-line no-restricted-syntax
    for (const connection of connections) {
      const connectedTable = app?.objects?.find((obj) => obj?.key === connection.object);
      if (!connectedTable) {
        break;
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const currentField of connectedTable.fields) {
        templateKeyVariablesTemp.push(`${connection.key}.${currentField.key}`);
        templateNameVariablesTemp.push(
          `${t('components.data_table.right_sidebar.connections.connection')} ${connection.name}.${currentField.name}`
        );
      }
    }
    return {
      templateKeyVariables: templateKeyVariablesTemp,
      templateNameVariables: templateNameVariablesTemp
    };
  }, [app?.objects, objectKey, t]);

  return (
    <RichTextEditor
      extensions={[Document, Paragraph, Text, History]}
      onCreate={({ editor }) => {
        // We save the editor instance so we can use it anywhere
        editorRef.current = editor;
      }}
      slotBefore={
        <div
          className={cn(
            'flex shrink-0 flex-wrap items-center gap-1 overflow-auto rounded-tl-lg rounded-tr-lg border-b border-t border-subtle bg-default p-2 pb-2'
          )}
        >
          <Popover>
            <Popover.Trigger>
              <ToolbarDropdownButton>
                {t('components.data_table.attributes.field_settings.rich_text_formula.button')}
              </ToolbarDropdownButton>
            </Popover.Trigger>
            <Popover.Content>
              <FormulaSelector editorRef={editorRef} equationType={functionTypes} />
            </Popover.Content>
          </Popover>
          <Popover>
            <Popover.Trigger>
              <ToolbarDropdownButton>
                {t('components.data_table.attributes.field_settings.rich_text_fields.button')}
              </ToolbarDropdownButton>
            </Popover.Trigger>

            <Popover.Content>
              <FieldSelector editorRef={editorRef} field={field} />
            </Popover.Content>
          </Popover>
        </div>
      }
      content={
        // Here we replace the field key with the field name in the editor.
        // This is because we want the user to see the field name instead of the field key. {Number 1} instead of {field_321}
        replaceTemplateVariables({
          text: content,
          variableList: templateKeyVariables,
          valueList: templateNameVariables.map((name) => `{${name}}`)
        })
      }
      onUpdate={({ editor }) => {
        onChange(
          // Here we replace the field name back to the field key before saving it to the database. {field_321} instead of {Number 1}
          replaceTemplateVariables({
            text: editor.getText(),
            variableList: templateNameVariables,
            valueList: templateKeyVariables.map((key) => `{${key}}`)
          })
        );
      }}
    />
  );
}
